import { memo } from "react";
import { Navigate } from "react-router-dom";
import type { ComponentType } from "react";
import type { LegacyPageConfig, LegacyPageContext } from "./legacyPageRegistryTypes";

interface UseLegacyPageRoutingResult {
  currentPageName: string;
  pageConfig?: LegacyPageConfig;
  PageComponent?: ComponentType;
  isValidPage: boolean;
}

interface CreateLegacyDynamicPageRouterParams {
  usePageRouting: () => UseLegacyPageRoutingResult;
  usePageContext: () => LegacyPageContext;
  renderLoading?: () => JSX.Element;
  renderNotFound?: () => JSX.Element;
  /**
   * Guard global do legado (ex.: termos/autorização).
   * Retorna um elemento para renderizar quando bloquear, ou null quando permitir.
   */
  renderGlobalGuards?: (context: LegacyPageContext) => JSX.Element | null;
}

/**
 * Fábrica de componente de rota dinâmica para o legado.
 *
 * Responsabilidades:
 * - resolver PageComponent via hook de routing
 * - aplicar guard global e guard por página
 * - centralizar fallback de notFound
 *
 * Observação:
 * - Loading (ex.: sessão carregando) fica neste componente para evitar
 *   lógica duplicada em hooks (substitui o antigo `usePages`).
 */
export function createLegacyDynamicPageRouter(params: CreateLegacyDynamicPageRouterParams) {
  const { usePageRouting, usePageContext, renderLoading, renderNotFound, renderGlobalGuards } =
    params;

  const LegacyDynamicPageRouter = memo(() => {
    const context = usePageContext();
    const { PageComponent, isValidPage, pageConfig } = usePageRouting();

    // Loading global (sessão)
    if (context.session?.loading) {
      if (renderLoading) {
        return renderLoading();
      }
      return (
        <div style={{ width: "100%", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          Carregando...
        </div>
      );
    }

    // Not found
    if (!isValidPage || !PageComponent || !pageConfig) {
      if (renderNotFound) {
        return renderNotFound();
      }
      return <Navigate to="/404" replace />;
    }

    // Guard global
    if (renderGlobalGuards) {
      const guardElement = renderGlobalGuards(context);
      if (guardElement) {
        return guardElement;
      }
    }

    // Guard por página
    const pageGuard = pageConfig.meta?.guard;
    if (pageGuard) {
      const guardResult = pageGuard(context);
      if (!guardResult.allow) {
        return <>{guardResult.render}</>;
      }
    }

    return <PageComponent />;
  });

  LegacyDynamicPageRouter.displayName = "LegacyDynamicPageRouter";

  return LegacyDynamicPageRouter;
}

